import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { FvElevatedComponent } from '@fv/ui-kit/components/elevated/elevated.component';
import { fvFormatOutageWindow } from '@fv/ui-kit/utils/format-time';

import { STATUS_PAGE_LOCALE } from '../../constants/status-page.constants';
import { SYSTEM_HEALTH_COPY } from '../../constants/system-health.copy';
import { SystemStatusPageQaKeys } from '../../qa-keys';
import { ReportLine, ReportWindow } from '../../types/system-health.types';

/**
 * The previous 24 hours as a daily report: only dependencies that had issues, every
 * slot listed, a clean day as one sentence. Rendered only on the internal (detail-key)
 * view — history is not a public route.
 */
@Component({
  selector: 'fv-status-report',
  standalone: true,
  imports: [FvElevatedComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './status-report.component.html',
  styleUrl: './status-report.component.scss',
})
export class StatusReportComponent {
  readonly qaKeys = SystemStatusPageQaKeys;

  readonly lines = input.required<ReportLine[]>();
  /** True while the history fetch is failing — the section stays, with a note instead of stale lines. */
  readonly isUnavailable = input(false);
  /** True when the window held more outages than the page carried, so no claim about the rest holds. */
  readonly isTruncated = input(false);
  /** The page clock: a window is only rendered bare when it is still today. */
  readonly now = input.required<number>();

  readonly copy = SYSTEM_HEALTH_COPY.report;

  readonly renderedLines = computed(() =>
    this.lines().map(line => ({
      key: line.key,
      label: line.label,
      isGap: line.isGap,
      windows: line.windows.map(window => this.formatWindow(window)).join(this.copy.windowSeparator),
    }))
  );

  private formatWindow(window: ReportWindow): string {
    const slot = fvFormatOutageWindow(
      window.startedAt,
      window.endedAt,
      this.copy.ongoingSuffix,
      STATUS_PAGE_LOCALE,
      this.now()
    );
    return window.services.length > 0 ? `${slot} (${window.services.join(this.copy.servicesSeparator)})` : slot;
  }
}
